// ItemManager.js
import itemsData from '../data/items.json';

export class ItemManager {
    constructor() {
        this.items = new Map();
        this.metadata = null;
        this.textureKeys = new Map(); // Store generated texture keys
        this.loadedTextures = new Set(); // Track which textures have been loaded
        this.initialize();
    }

    // Get category name from category code
    getCategoryFromCode(categoryCode) {
        if (!this.metadata || !this.metadata.categoryIndex) {
            console.warn('Category index not available in metadata');
            return null;
        }
        
        const category = this.metadata.categoryIndex[categoryCode.toString()];
        if (!category) {
            console.warn(`Invalid category code: ${categoryCode}`);
            return null;
        }
        
        return category;
    }

    // Get item data by category code and item code (new format)
    getItemDataByCategory(categoryCode, itemCode) {
        const category = this.getCategoryFromCode(categoryCode);
        if (!category) {
            return null;
        }

        const fullId = `${category}_${itemCode}`;
        const item = this.items.get(fullId);
        
        if (!item) {
            console.warn(`Item not found: ${category}_${itemCode}`);
            return null;
        }

        return {
            // Include item properties first, then override with computed values
            ...item,
            id: fullId,
            name: item.name || 'Unknown Item',
            rarity: item.rarity || 'Normal',
            type: item.type || category,
            description: item.description || '',
            value: item.value || 0,
            category: category,
            categoryCode: categoryCode,
            itemCode: itemCode
        };
    }

    // Get frame key for a given rarity
    getFrameKey(rarity) {
        if (!rarity) return 'itemFrameNormal'; // Default frame if rarity is undefined
        return `itemFrame${rarity}`;
    }

    // Generate a unique texture key for a composed item
    generateTextureKey(itemId, rarity) {
        if (!itemId || !rarity) return null;
        return `item_${itemId}_${rarity.toLowerCase()}`;
    }

    // Compose item texture with appropriate frame
    composeItemTexture(scene, item) {
        if (!item || !item.id || !item.rarity) {
            console.warn('Invalid item data for texture composition:', item);
            return null;
        }

        const textureKey = this.generateTextureKey(item.id, item.rarity);
        
        // Check if we've already composed this texture
        if (this.textureKeys.has(textureKey)) {
            return textureKey;
        }

        // Create a new texture by combining frame and item sprite
        try {
            const frameKey = this.getFrameKey(item.rarity);
            
            // Verify that both textures exist in the scene
            if (!scene.textures.exists(frameKey) || !scene.textures.exists(item.textureKey)) {
                console.warn('Required textures not loaded:', { frameKey, itemTextureKey: item.textureKey });
                return null;
            }

            // Create a temporary canvas for composition
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = 32;
            canvas.height = 32;

            // Load images and compose them
            const frameImage = scene.textures.get(frameKey).getSourceImage();
            const itemImage = scene.textures.get(item.textureKey).getSourceImage();

            // Draw frame first
            ctx.drawImage(frameImage, 0, 0, 32, 32);

            // Center the item sprite on the frame
            const x = (32 - itemImage.width) / 2;
            const y = (32 - itemImage.height) / 2;
            ctx.drawImage(itemImage, x, y);

            // Add the composed texture to the scene
            scene.textures.addCanvas(textureKey, canvas);
            this.textureKeys.set(textureKey, true);

            return textureKey;
        } catch (error) {
            console.error('Error composing item texture:', error);
            return null;
        }
    }

    // Get complete item data for inventory
    // Updated to handle both old format (Consumable_6) and new format (category_code + item_code)
    getItemData(itemId, categoryCode = null) {
        // Handle the new format: getItemData(itemCode, categoryCode)
        if (categoryCode !== null) {
            return this.getItemDataByCategory(categoryCode, itemId);
        }

        // Handle both string itemIds and inventory item objects
        const id = typeof itemId === 'string' ? itemId : 
                (itemId && itemId.itemCode) ? itemId.itemCode : null;
                    
        if (!id || typeof id !== 'string') {
            console.warn('Invalid itemId provided to getItemData:', itemId);
            return null;
        }

        try {
            let category, itemNum;
            
            // Handle both formats: "Category_Number" and "Item_Number"
            if (id.startsWith('Item_')) {
                // If it's "Item_X" format, assume it's a Consumable
                category = 'Consumable';
                itemNum = id.split('_')[1];
            } else if (id.includes('_')) {
                // Handle "Category_Number" format
                [category, itemNum] = id.split('_');
            } else {
                // Handle just a number (database format) - assume Consumable for now
                category = 'Consumable';
                itemNum = id;
            }
            
            if (!category || !itemNum) {
                console.warn('Invalid itemId format:', id);
                return null;
            }

            // Convert legacy "Item_X" format to "Consumable_X"
            const fullId = id.startsWith('Item_') ? `Consumable_${itemNum}` : `${category}_${itemNum}`;
            
            const item = this.items.get(fullId);
            
            if (!item) {
                console.warn(`Item not found: ${id} (tried ${fullId})`);
                return null;
            }

            return {
                // Include item properties first, then override with computed values
                ...item,
                id: fullId,
                name: item.name || 'Unknown Item',
                rarity: item.rarity || 'Normal',
                type: item.type || category,
                description: item.description || '',
                value: item.value || 0,
                category: category
            };
        } catch (error) {
            console.error('Error in getItemData:', error);
            return null;
        }
    }

    // Preload item assets for a scene
    preloadAssets(scene) {
        if (!scene || !scene.load || !scene.textures) {
            console.error('Invalid scene provided to preloadAssets');
            return;
        }
    
        // Track which textures we're going to load in this batch
        const texturesToLoad = new Set();
    
        // Check frames first
        if (this.metadata && this.metadata.rarityOrder) {
            this.metadata.rarityOrder.forEach(rarity => {
                const frameKey = this.getFrameKey(rarity);
                // Only load if texture doesn't already exist
                if (frameKey && !scene.textures.exists(frameKey)) {
                    texturesToLoad.add({
                        key: frameKey,
                        path: `/sprites/ui/inventory/${frameKey}.png`
                    });
                }
            });
        }
    
        // Check item sprites
        this.items.forEach((item, key) => {
            if (item && item.textureKey && item.spritePath) {
                // Only load if texture doesn't already exist
                if (!scene.textures.exists(item.textureKey)) {
                    texturesToLoad.add({
                        key: item.textureKey,
                        path: item.spritePath
                    });
                }
            }
        });
    
        // Load all new textures at once
        texturesToLoad.forEach(texture => {
            try {
                scene.load.image(texture.key, texture.path);
            } catch (error) {
                console.warn(`Failed to load texture: ${texture.key}`, error);
            }
        });
    }
    
    // Create composed item sprite
    createItemSprite(scene, itemId, x, y, useAsSeedBag = false) {
        const item = this.getItemData(itemId);
        if (!item) {
            console.warn('Could not create sprite for item:', itemId);
            return null;
        }

        let composedTextureKey;
        if (item.type === 'Seeds') {
            // Seeds always use seed bag texture
            composedTextureKey = this.createSeedBagTexture(scene, item);
        } else if (useAsSeedBag && item.type === 'Crop') {
            // Crops can be displayed as seed bags when requested
            composedTextureKey = this.createSeedBagTexture(scene, item);
        } else if (item.type === 'Crop') {
            // Crops use produce frame (frame 0)
            composedTextureKey = this.createProduceTexture(scene, item);
        } else {
            // Regular items use normal composition
            composedTextureKey = this.composeItemTexture(scene, item);
        }
        
        if (!composedTextureKey) {
            console.warn('Could not compose texture for item:', itemId);
            return null;
        }

        return {
            sprite: scene.add.image(x, y, composedTextureKey),
            data: item
        };
    }

    // Create produce texture from farming spritesheet frame 0
    createProduceTexture(scene, item) {
        if (!item || item.type !== 'Crop') {
            return null;
        }

        const produceTextureKey = `produce_${item.textureKey}`;
        
        // Check if we've already created this texture
        if (scene.textures.exists(produceTextureKey)) {
            return produceTextureKey;
        }

        try {
            // Verify that the farming texture exists
            if (!scene.textures.exists(item.textureKey)) {
                console.warn('Farming texture not loaded:', item.textureKey);
                return null;
            }

            // Create a canvas to draw frame 0 (produce) from the farming spritesheet
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = 32;
            canvas.height = 32;

            // Load the farming spritesheet
            const farmingImage = scene.textures.get(item.textureKey).getSourceImage();
            
            // Use frame 0 for produce (harvested item)
            const produceFrame = item.produce?.frame || 0;
            const frameX = produceFrame * 16; // Each frame is 16px wide
            
            // Draw produce frame - we want the bottom 16x16 pixels for the inventory icon
            ctx.drawImage(farmingImage, frameX, 16, 16, 16, 8, 8, 16, 16);

            // Add the composed texture to the scene
            scene.textures.addCanvas(produceTextureKey, canvas);

            return produceTextureKey;
            
        } catch (error) {
            console.error('Error creating produce texture:', error);
            return null;
        }
    }

    // Create seed bag texture from farming spritesheet frame 2
    createSeedBagTexture(scene, item) {
        if (!item || (item.type !== 'Crop' && item.type !== 'Seeds')) {
            return null;
        }

        const seedTextureKey = `seed_${item.textureKey}`;
        
        // Check if we've already created this texture
        if (scene.textures.exists(seedTextureKey)) {
            return seedTextureKey;
        }

        try {
            // Verify that the farming texture exists
            if (!scene.textures.exists(item.textureKey)) {
                console.warn('Farming texture not loaded:', item.textureKey);
                return null;
            }

            // Create a canvas to draw frame 2 (seed bag) from the farming spritesheet
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = 32;
            canvas.height = 32;

            // Load the farming spritesheet
            const farmingImage = scene.textures.get(item.textureKey).getSourceImage();
            
            // Use the seed frame from the seed item, crop structure, or default to frame 1
            let seedFrame = 1; // Default seed frame
            if (item.type === 'Seeds' && item.frame !== undefined) {
                seedFrame = item.frame;
            } else if (item.seed?.frame !== undefined) {
                seedFrame = item.seed.frame;
            }
            const frameX = seedFrame * 16; // Each frame is 16px wide
            
            // Draw seed bag frame - we want the bottom 16x16 pixels for the inventory icon
            ctx.drawImage(farmingImage, frameX, 16, 16, 16, 8, 8, 16, 16);

            // Add the composed texture to the scene
            scene.textures.addCanvas(seedTextureKey, canvas);

            return seedTextureKey;
            
        } catch (error) {
            console.error('Error creating seed bag texture:', error);
            return null;
        }
    }

    // Get crop data for farming system
    getCropData(itemId) {
        const item = this.getItemData(itemId);
        if (!item || item.type !== 'Crop') {
            return null;
        }
        return item;
    }

    // Get seed data from crop item
    getSeedData(itemId) {
        const cropData = this.getCropData(itemId);
        return cropData ? cropData.seed : null;
    }

    // Get produce data from crop item
    getProduceData(itemId) {
        const cropData = this.getCropData(itemId);
        return cropData ? cropData.produce : null;
    }

    // Initialize on construction
    initialize() {
        this.loadItems(itemsData);
    }

    // Load items from the items data
    loadItems(itemsData) {
        if (!itemsData || !itemsData.metadata) {
            console.error('Invalid items data format');
            return;
        }

        this.metadata = itemsData.metadata;
        
        // Process items from the 'items' section
        if (itemsData.items) {
            Object.entries(itemsData.items).forEach(([category, items]) => {
                Object.entries(items).forEach(([id, item]) => {
                    if (!item || !item.textureKey || !item.spritePath) {
                        console.warn('Invalid item data:', { category, id, item });
                        return;
                    }

                    const fullId = `${category}_${id}`;
                    this.items.set(fullId, {
                        ...item,
                        category
                    });
                });
            });
        }

        // Process root-level sections (Tool, Weapon, Armor, etc.)
        // Check for all categories defined in the categoryIndex
        if (this.metadata.categoryIndex) {
            Object.values(this.metadata.categoryIndex).forEach(category => {
                // Skip categories that are already processed in the 'items' section
                if (category === 'Consumable' || category === 'Seeds') {
                    return;
                }
                
                // Check if this category exists as a root-level section
                if (itemsData[category]) {
                    Object.entries(itemsData[category]).forEach(([id, item]) => {
                        if (!item) {
                            console.warn('Invalid item data:', { category, id, item });
                            return;
                        }

                        const fullId = `${category}_${id}`;
                        this.items.set(fullId, {
                            ...item,
                            category
                        });
                    });
                }
            });
        }
        
    }

    // Get filtered items based on criteria
    getFilteredItems(criteria = {}) {
        return Array.from(this.items.entries())
            .filter(([_, item]) => {
                return Object.entries(criteria).every(([key, value]) => {
                    if (!item) return false;
                    // Handle subtype prefix matching
                    if (key === 'subtypePrefix' && item.subtype) {
                        return item.subtype.startsWith(value);
                    }
                    // Handle exact matches for other fields
                    return item[key] === value;
                });
            })
            .map(([id, item]) => ({ id, ...item }));
    }

    // Select a random item from a filtered list using weighted probabilities
    selectRandomItem(criteria = {}) {
        const filteredItems = this.getFilteredItems(criteria);
        
        if (filteredItems.length === 0) {
            console.warn('No items match the criteria:', criteria);
            return null;
        }

        // Calculate total weight and cumulative weights
        let totalWeight = 0;
        const weightedItems = filteredItems.map(item => {
            const weight = item.dropRate || 1;
            const range = {
                min: totalWeight,
                max: totalWeight + weight
            };
            totalWeight += weight;
            return { ...item, weightRange: range };
        });

        // Make sure we have valid weights
        if (totalWeight <= 0) {
            console.warn('No valid weights found in filtered items');
            return filteredItems[0]; // Return first item as fallback
        }

        // Roll for an item
        const roll = Math.random() * totalWeight;
        
        // Find the item whose range contains our roll
        return weightedItems.find(item => 
            roll >= item.weightRange.min && roll < item.weightRange.max
        ) || weightedItems[0]; // Fallback to first item if nothing found
    }

    // Calculate challenge rating for an item (useful for gameplay mechanics)
    calculateFishChallengeRating(item) {
        if (!item) return 1.0; // Default challenge

        // Get rarity index from metadata
        const rarityIndex = this.metadata?.rarityOrder?.indexOf(item.rarity) ?? -1;
        const maxRarityIndex = (this.metadata?.rarityOrder?.length ?? 1) - 1;
        
        // Base challenge from rarity (0.5 to 2.0)
        const rarityFactor = rarityIndex >= 0 ? 
            0.5 + (2.0 * (rarityIndex / maxRarityIndex)) : 1.0;
        
        // Value factor (logarithmic scaling)
        const valueFactor = item.value ? 
            0.5 + (Math.log10(item.value) / 10) : 1.0;

        // Combine factors and clamp result
        return Math.min(Math.max(
            rarityFactor * valueFactor,
            0.3  // Minimum challenge
        ), 2.0); // Maximum challenge
    }

    // Debug method to print drop rates for filtered items
    debugPrintDropRates(criteria = {}) {
        const filteredItems = this.getFilteredItems(criteria);
        const totalWeight = filteredItems.reduce((sum, item) => sum + (item.dropRate || 0), 0);

        filteredItems.forEach(item => {
            const chance = ((item.dropRate || 0) / totalWeight) * 100;
        });
    }
}

// Export a singleton instance
export const itemManager = new ItemManager();